import { useState, useEffect } from 'react';
import api from '../../../../api';
import IconStar from '../../../Icon/IconStar';
import IconCalendar from '../../../Icon/IconCalendar';
import IconChartSquare from '../../../Icon/IconChartSquare';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';

function UserLevelQualityDashboards() {
  const [isRtl] = useState(false);
  const [errorData, setErrorData] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { accessToken } = useAuth();

  // Fetch Dashboard Quality Data
  useEffect(() => {
    if (!accessToken) return;

    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        let baseUrls = getRoundRobinBaseUrl();
        const response = await api.get(`${baseUrls}/userDashboard/getUserDashboard`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setDashboardData(response.data);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [accessToken]);

  // Fetch Top 5 Errors
  useEffect(() => {
    let baseUrls = getRoundRobinBaseUrl();
    if (!accessToken) return;
    setLoading(true);

    api
      .get(`${baseUrls}/userDashboard/getTop5Error`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((response) => {
        if (response.data.success && Array.isArray(response.data.data)) {
          setErrorData(response.data.data);
        } else {
          setErrorData([]);
        }
      })
      .catch((err) => console.error('Error fetching top 5 errors:', err))
      .finally(() => setLoading(false));
  }, [accessToken]);

  if (loading) {
    return (
      <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-gray-200 bg-opacity-50 z-50">
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent animate-spin rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return <p className="text-red-500 text-center">{error}</p>;
  }

  // Extract quality percentages
  const previousDayQuality = dashboardData?.previousDay?.[0]?.qualityPercent || 'N/A';
  const mtdQuality = dashboardData?.MTD?.[0]?.qualityPercent || 'N/A';
  const ytdQuality = dashboardData?.YTD?.[0]?.qualityPercent || 'N/A';

  // Colors for error categories
  const colors = ['text-red-600', 'text-blue-600', 'text-green-600', 'text-yellow-600', 'text-purple-600'];

  return (
    <>
      {/* Quality Cards */}
      <div className="grid xl:grid-cols-3 gap-10 mb-6">
        {/* RED - Previous Day Quality */}
        <div className="flex bg-white shadow-md rounded-lg overflow-hidden border-l-8 border-[#EE1D23] hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-center w-20 bg-[#EE1D23]">
            <IconStar className="w-8 h-8 text-white" />
          </div>
          <div className="flex flex-col justify-center px-5 py-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Previous Day Quality</h3>
            <p className="text-3xl font-bold text-[#EE1D23]">{previousDayQuality}</p>
          </div>
        </div>

        {/* BLUE - MTD Quality */}
        <div className="flex bg-white shadow-md rounded-lg overflow-hidden border-l-8 border-[#1A2B5C] hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-center w-20 bg-[#1A2B5C]">
            <IconCalendar className="w-8 h-8 text-white" />
          </div>
          <div className="flex flex-col justify-center px-5 py-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">MTD Quality</h3>
            <p className="text-3xl font-bold text-[#1A2B5C]">{mtdQuality}</p>
          </div>
        </div>

        {/* GRAY - YTD Quality */}
        <div className="flex bg-white shadow-md rounded-lg overflow-hidden border-l-8 border-[#808080] hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-center w-20 bg-[#808080]">
            <IconChartSquare className="w-8 h-8 text-white" />
          </div>
          <div className="flex flex-col justify-center px-5 py-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">YTD Quality</h3>
            <p className="text-3xl font-bold text-[#808080]">{ytdQuality}</p>
          </div>
        </div>
      </div>

      {/* Top 5 Errors Panel */}
      <div className="panel h-full xl:col-span-4 p-8 bg-white shadow-2xl rounded-xl border border-gray-200 mb-6 mt-2">
        <h2 className="text-2xl font-bold mb-6 text-[#1A2B5C] border-b-2 border-[#1A2B5C] pb-3 uppercase">
          Top 5 Error Categories
        </h2>

        <div className="grid xl:grid-cols-2 gap-8">
          {errorData.length > 0 ? (
            errorData.map((error, index) => {
              const borderColors = ['#EE1D23', '#1A2B5C', '#808080', '#1A2B5C', '#EE1D23'];
              const color = borderColors[index % borderColors.length];
              return (
                <div
                  key={index}
                  className="flex flex-col h-full p-6 bg-white rounded-lg shadow-md hover:shadow-xl border-l-8 transition-all duration-300 hover:scale-[1.02]"
                  style={{ borderColor: color }}
                >
                  <h3 className="text-lg font-bold mb-4 uppercase tracking-wide" style={{ color }}>
                    {error.errorCategory}
                  </h3>
                  <p className="text-base text-gray-700 font-medium">
                    Current Month: <span className="font-semibold text-[#1A2B5C]">{error.currentMonthCount}</span>
                  </p>
                  <p className="text-base text-gray-700 font-medium">
                    Last Month: <span className="font-semibold text-[#808080]">{error.lastMonthCount}</span>
                  </p>
                  <p className="text-base text-gray-700 font-medium">
                    Difference: <span className="font-semibold text-[#EE1D23]">{error.difference}</span>
                  </p>
                </div>
              );
            })
          ) : (
            <p className="text-base text-gray-500 text-center">No error data available.</p>
          )}
        </div>
      </div>

      {/* Dashboard Components */}
      {/* <div className="grid xl:grid-cols-4 gap-6 mb-4">
        <div className="h-full xl:col-span-1">
          <Arealinegraph data={OverallQualityData} color="#e7515a" text="Overall Quality" Logo={IconStar} />
        </div>
      </div> */}

      {/* <div className="grid xl:grid-cols-4 gap-6 mb-4">
        <div className="panel h-full xl:col-span-4">
          <DashboardWrapper title="Quality" showMonth={true} showDropdown={true} monthData={UserQuality}>
            <Horizontalbarchart data={UserQualityBarData} isDark={false} isRtl={isRtl} xTitle="Score" yTitle="Period" />
          </DashboardWrapper>
        </div>
      </div> */}
    </>
  );
}

export default UserLevelQualityDashboards;
